import React from "react";
import { z } from "zod/v3";

import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { Textarea } from "@workspace/ui/components/textarea";

import type { TabUpdateInput } from "../../types";
import type { EposTab } from "../epos-context";
import { useEposContext } from "../epos-context";

const TabUpdateSchema = z.object({
	name: z.string().min(1, "Name is required"),
	notes: z.string(),
});

type TabUpdateValues = z.infer<typeof TabUpdateSchema>;

export function EditTabDialog({
	isOpen,
	onOpenChange,
	tab,
}: {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	tab: EposTab | null;
}) {
	const { updateTab, isUpdatingTab } = useEposContext();

	const form = useAppForm({
		defaultValues: {
			name: tab?.name ?? "",
			notes: tab?.notes ?? "",
		} satisfies TabUpdateValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: TabUpdateSchema,
		},
		onSubmit: async ({ value }) => {
			if (!tab) return;

			const payload: TabUpdateInput = {
				name: value.name,
				notes: value.notes.trim() ? value.notes : null,
				updatedAt: new Date().toISOString(),
			};

			const result = await updateTab({ tabId: tab.id, tab: payload });
			if (!result.success) return;

			handleOpenChange(false);
		},
	});

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			if (!open) {
				form.reset();
				onOpenChange(false);
				return;
			}

			form.reset();
			form.setFieldValue("name", tab?.name ?? "");
			form.setFieldValue("notes", tab?.notes ?? "");
			onOpenChange(true);
		},
		[form, onOpenChange, tab],
	);

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>Edit Tab</DialogTitle>
					<DialogDescription>Update tab name and notes.</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Name</field.FormLabel>
								<field.FormControl>
									<Input
										placeholder="Customer name"
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="notes">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Notes</field.FormLabel>
								<field.FormControl>
									<Textarea
										placeholder="Optional notes"
										value={field.state.value || ""}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={isUpdatingTab}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={isUpdatingTab || !tab}>
							{isUpdatingTab && <Spinner />}
							Save
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
}
